import { useRef, useState } from 'react';
import { Avatar, Box, Button, Divider, Hidden, lighten, List, ListItem, ListItemText, Popover, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscription } from 'src/contexts/SubscriptionContext';
import { SubscriptionStatus } from 'src/models/userSubscription';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

const StatusChip = styled(Box)<{ status: SubscriptionStatus }>(
  ({ theme, status }) => {
    const getStatusColor = () => {
      switch (status) {
        case SubscriptionStatus.PaidPremium:
          return theme.palette.success.main;
        case SubscriptionStatus.FreePremium:
          return theme.palette.info.main;
        case SubscriptionStatus.Expired:
        default:
          return theme.palette.error.main;
      }
    };

    return `
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      background-color: ${getStatusColor()}20;
      color: ${getStatusColor()};
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid ${getStatusColor()}40;
    `;
  }
);

export const HeaderUserbox = () => {
  const { user, logout, isLoading: isAuthLoading, isAuthenticated } = useAuth0();
  const { subscription } = useSubscription();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  if (isAuthLoading || !isAuthenticated || !user) {
    return null;
  }

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const getStatusDisplay = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.FreePremium:
        return 'Free Premium';
      case SubscriptionStatus.PaidPremium:
        return 'Premium';
      case SubscriptionStatus.Expired:
      default:
        return 'Expired';
    }
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={user?.name} src={user?.picture} />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1" noWrap sx={{ maxWidth: 160 }}>
              {user?.name}
            </UserBoxLabel>
            <UserBoxDescription variant="body2" noWrap sx={{ maxWidth: 160 }}>
              {user?.jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={user?.name} src={user?.picture} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{user?.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user?.jobtitle}
            </UserBoxDescription>
            {subscription && (
              <StatusChip status={subscription.status} sx={{ mt: 0.5 }}>
                {getStatusDisplay(subscription.status)}
              </StatusChip>
            )}
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          {subscription?.status !== SubscriptionStatus.PaidPremium && (
            <ListItem
              button
              to="/settings?tab=subscription"
              component={NavLink}
              onClick={handleClose}
            >
              <SettingsTwoToneIcon fontSize="small" />
              <ListItemText primary="Manage Subscription" />
            </ListItem>
          )}
        </List>
        <Divider />
        <Box sx={{ m: 1, pl: '5px' }}>
          <Button color="primary" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default HeaderUserbox;
